/**
 * HOW A PERSON REACHES A CONTROL, ANSWERED IN ONE PLACE (3.0.0, ADR-0108).
 *
 * The hub shows one job at a time, so anything that wants to press a control
 * has to arrive at it first. The app already works this out on every paint —
 * which section owns an element, whether its job is the stance, whether that
 * job is behind its own opener, whether a fold sits above it.
 *
 * The browser walks worked it out again, from outside, and that second model is
 * where an afternoon went. Fourteen rounds, and every one of them was the same
 * mistake wearing different clothes:
 *
 *   - AN ABSENCE READ AS AN ANSWER. The shim declining to navigate looks
 *     exactly like "no navigation needed", so every wrong answer was silent.
 *   - NOT HIDDEN IS NOT OPEN. A job can be stood in without being opened, and
 *     `#triage-card` is a button that EXISTS in the markup and is EMPTY until
 *     the inbox is opened — and an empty button has zero size, so waiting for
 *     it to become visible waits for something nothing will render.
 *   - ARRIVING IS NOT REVEALING. Pressing a job's opener is arriving. Opening a
 *     `<details>` reveals, and an assertion may legitimately be asking whether
 *     something is still folded away.
 *
 * None of those are careless. They are what happens when two things model one
 * thing: they drift, and the drift is invisible until a slower machine runs it.
 *
 * So the rules live here, `src/ui/hub.ts` uses them to decide what belongs to
 * the stance, and the walks use them to arrive. One source. When the shape of
 * the app changes, one file changes with it.
 *
 * NOTHING TEST-ONLY SHIPS. The walks bundle this file themselves and inject it;
 * the app imports it like any other module. There is no hook on `window` and no
 * flag in the app that exists for a test's benefit.
 */

/** How far the caller got, and what it still needs. */
export type Arrival =
  /** Nothing more to do: already there, or everything needed was pressed. */
  | { at: 'here' }
  /** The job exists but its door is not on the hub yet. Wait, then ask again. */
  | { at: 'waiting'; door: string }
  /** Nothing on the page answers to this, and no job owns it. */
  | { at: 'nowhere' };

/**
 * WHICH JOBS AN ELEMENT BELONGS TO, which is not only "what section is it in".
 *
 * `<main>` carries furniture BETWEEN the sections and several pieces belong to
 * a job rather than to the page — `#triage-undo` is the undo bar for sorting
 * and sits outside `#triage` entirely. Hiding everything but the active section
 * took it with them, and filing something by mistake left no way back.
 *
 * MOSTLY ALREADY DECLARED: `data-narrows` has said for releases which surface
 * an element belongs to, so the situation controls and the lens row need
 * nothing new. `data-stance-part` covers the few that narrow nothing and simply
 * belong.
 *
 * A LIST, not one id: `data-narrows="#nextup,#held"` belongs to both, and
 * collapsing that to a first answer is what made `belongs` and the walk's copy
 * disagree in the first place.
 */
export function jobsOf(el: Element): string[] {
  const sec = el.closest('section[data-stance-name]');
  if (sec) return [sec.id];
  // The hub's own controls mean COME UP. Its doors are hidden while you are
  // inside a job, which is correct, and both callers have to know it.
  if (el.closest('#hub') || el.closest('[data-hub-part]')) return [HUB];
  const owner = el.closest('[data-stance-part], [data-narrows]');
  if (!owner) return [];
  const part = owner.getAttribute('data-stance-part');
  if (part) return [part];
  return (owner.getAttribute('data-narrows') ?? '')
    .split(',').map((t) => t.trim().replace(/^#/, '')).filter(Boolean);
}

/** Coming up is a destination like any other, and needs a name to be one. */
export const HUB = ' hub';

/**
 * PLAYWRIGHT'S PSEUDOS ARE NOT CSS, and `querySelector` THROWS on them rather
 * than returning null — so a `?? fallback` after one never ran, the throw
 * escaped to the caller's catch, and the walk declined to navigate on every
 * `:has-text()` selector it had. Silently. `:has()` is real CSS and stays.
 */
function find(sel: string, doc: Document): Element | null {
  const ask = (q: string): Element | null => {
    try { return doc.querySelector(q); } catch { return null; }
  };
  const bare = (sel ?? '')
    .replace(/:has-text\([^)]*\)/g, '')
    .replace(/:text-is\([^)]*\)/g, '')
    .replace(/:text\([^)]*\)/g, '')
    .replace(/:nth-match\([^)]*\)/g, '')
    .replace(/:visible/g, '')
    .replace(/\s*>>.*$/, '')
    .trim();
  return ask(sel) ?? ask(bare) ?? ask(bare.split(':')[0] ?? bare);
}

/**
 * THE JOB A SELECTOR NAMES, even when none of it has been built yet.
 *
 * `#triage-card` is rendered BY opening the inbox, and opening the inbox is
 * what entering that job does — so looking for the element before deciding
 * where to go waits for a thing only the waiting prevented. Ids run
 * `<section>-<part>` throughout this app, so the job is in the name.
 */
function jobNamedBy(sel: string, doc: Document): string | null {
  const m = /^#([A-Za-z0-9]+)-/.exec(sel.trim());
  const id = m?.[1];
  if (!id) return null;
  const named = doc.getElementById(id);
  return named && named.matches('section[data-stance-name]') ? id : null;
}

/**
 * Arrive at whatever this selector names. Safe to call when already there.
 *
 * Synchronous and idempotent on purpose: WHEN to arrive is the caller's
 * business — a walk retries, the app does not — and only HOW lives here.
 */
export function reach(selector: string, doc: Document = document): Arrival {
  const runway = doc.querySelector('#runway');
  // No hub means no stance: an empty store shows the whole page and there is
  // nothing to arrive at.
  if (!runway || !runway.hasAttribute('data-hub')) return { at: 'here' };
  const here = runway.getAttribute('data-stance');

  const el = find(selector, doc);
  const wants = el ? jobsOf(el) : [];
  const want: string | null = wants.length
    ? (wants.includes(here ?? '') ? here : (wants[0] ?? null))
    : jobNamedBy(selector, doc);

  if (want === HUB) {
    if (here !== null) doc.querySelector<HTMLElement>('#stance-back')?.click();
    return { at: 'here' };
  }

  if (want !== null && want !== here) {
    if (here !== null) doc.querySelector<HTMLElement>('#stance-back')?.click();
    const door = `#hub-doors .hub-go[data-stance-id="${want}"]`;
    const b = doc.querySelector<HTMLElement>(door);
    // A JOB BECOMES LIVE A BEAT AFTER THE WRITE THAT FILLS IT — capture
    // something and `#nextup` is not on the hub yet. Say so rather than
    // declining, because declining is indistinguishable from "nothing needed".
    if (!b) return { at: 'waiting', door };
    b.click();
  }

  if (want === null && !el) return { at: 'nowhere' };

  // OPEN IT. Standing in a job is not the same as having opened it. The same
  // `alreadyOpen` test the hub itself uses, so a job in progress is never reset
  // back to its first question by somebody merely arriving at it.
  const sec = doc.getElementById(want ?? '');
  const opener = sec?.querySelector<HTMLElement>('[data-stance-opener]');
  if (opener && !(opener as HTMLButtonElement).hidden && !sec?.querySelector('.route')) {
    opener.click();
  }
  return { at: 'here' };
}

/**
 * OPEN THE FOLDS ABOVE IT, by pressing the summary a finger presses.
 *
 * SEPARATE FROM ARRIVING, and that line cost a round to draw correctly.
 * Arriving is entering a job and opening it — the app does that itself on every
 * navigation, so doing it under a wait is the same act the wait was going to
 * see anyway. Revealing is different: `<details>` hides content deliberately,
 * and "the inventory arrives folded" is an assertion somebody wrote about
 * exactly that. So this belongs to the verbs, never to a wait.
 */
export function unfold(selector: string, doc: Document = document): void {
  const el = find(selector, doc);
  if (!el) return;
  for (let p = el.parentElement; p; p = p.parentElement) {
    if (p.tagName !== 'DETAILS' || (p as HTMLDetailsElement).open) continue;
    const sum = p.querySelector<HTMLElement>(':scope > summary');
    // NEVER THE FOLD THE TARGET IS THE HANDLE OF. Opening it here and then
    // letting the caller's own press land on the summary toggles it shut again,
    // and the caller then waits for a card it just closed the door on.
    if (sum && (sum === el || sum.contains(el))) continue;
    if (sum) sum.click(); else (p as HTMLDetailsElement).open = true;
  }
}
